/**
 * 새 버전 갈아타기 — "언제 갈아타나"와 "지금 새로고침해도 작업이 안 날아가나"를 가르는 판정 규칙.
 *
 * 규칙의 진본은 워크스페이스 프론트 공통 규칙 §13 «새 판 갈아타기»(ops/workflow/frontend-conventions.md).
 * 이 프로젝트 화면이 하는 일(2026-11-04 사용자 결정 — «깜빡거리면 안 되니까 … 화면을 이동할 때는 자동으로»):
 *  ① 새 버전은 뒤에서 알아채 두기만 한다 — 알아챘다고 바로 새로고침하지 않는다.
 *  ② 알아채면 머리 줄에 「업데이트」 버튼이 선다 — 누르면 곧바로 새 버전.
 *  ③ 다른 화면(경로)으로 옮기는 순간 저절로 갈아탄다 — 어차피 화면이 통째로 바뀌는 때라 깜빡임으로 안 보인다.
 *  ④ 옮길 때도 잃을 게 있으면(아래 UnsafeReason) 이번엔 건너뛰고 다음 옮길 때 다시 본다.
 *  ⑤ 가만히 보는 화면은 절대 저절로 새로고침하지 않는다. 예외는 «자리 비움»(입력 없이 30분) 하나 —
 *     판매점 PC 는 브라우저를 켜 둔 채 퇴근했다가 다음 날 그대로 쓰기 때문이다. 그때도 ④가 안전할 때만.
 *
 * 🔴 2026-09-05 ~ 2026-11-04 에는 «안전하면 보는 중에도 1.5초 "업데이트 중…" 뒤 새로고침, 위험하면
 *    모달로 묻기» 였다. 하루에도 여러 번 배포하니 손을 뗀 채 표를 읽던 화면이 저절로 번쩍이며 다시
 *    열렸다(손을 안 댄 10초는 «안 보는 중» 이 아니라 «읽는 중» 일 때가 많다). 그래서 위 ①~⑤로 바꿨다.
 *
 * 신호를 모으는 건 브라우저 쪽(src/lib/updateSafety.ts)이고, 여기는 순수 함수라 Node 시험으로 규칙을 고정한다.
 * 규칙은 **위험 쪽으로 기울여** 둔다 — 안전한데 건너뛰면 다음 옮길 때 갈아타면 그만이지만,
 * 위험한데 새로고침하면 작업이 날아간다.
 */

/** 새로고침을 미루는 이유. */
export type UnsafeReason =
  | "customer-page" // 고객이 직접 채우는 공개 화면(신청서·서명·페이백) — 저장 전 입력이 통째로 날아간다
  | "guarded-page" // 화면이 스스로 "떠나면 잃는다"고 선언(beforeunload 로 막는 화면 — 시세표 편집기 등)
  | "modal-open" // 창(팝업)이 떠 있다 — 대개 입력·선택·확인 중인 자리
  | "editing" // 커서가 있는 입력칸에 글이 들어 있다 — 쓰다 만 글
  | "request-in-flight" // 저장·업로드 요청이 서버 응답을 기다리는 중
  | "save-pending"; // 자동 저장이 몇 초 뒤 보내려고 기다리는 중(아직 요청이 안 나가 위 항목에 안 잡힌다)

export interface UpdateSafetySignals {
  /** 지금 주소 경로(pathname). */
  pathname: string;
  /** 직전 경로 — 화면을 옮긴 순간 판정할 때만 준다. 고객 입력 화면을 떠나는 길도 막으려는 것이다. */
  previousPathname?: string | null;
  /** 화면이 beforeunload 를 막았나(가짜 이벤트를 쏴서 defaultPrevented 를 본 결과). */
  unloadGuarded: boolean;
  /** 떠 있는 창(팝업) 수. 「업데이트」 버튼은 창이 아니라 여기 세지 않는다. */
  openModals: number;
  /** 커서가 있는 입력칸(input·textarea·contenteditable)에 글이 들어 있나. 빈 검색창에 커서만 있으면 false. */
  editing: boolean;
  /** 진행 중인 저장·업로드 요청 수(GET 이 아닌 fetch + react-query mutation). */
  inFlightWrites: number;
  /**
   * 보내려고 기다리는 자동 저장 수 — «바꾸면 1초 뒤 저장, 떠날 때 마지막으로 한 번 더» 화면(입력폼 설정)은
   * 그 1초 동안 요청이 아직 안 나가 inFlightWrites 에 안 잡힌다. 화면을 옮기는 순간이 곧 «떠날 때 저장» 이라
   * 그때 새로고침하면 마지막 저장이 끊긴다(2026-11-04 코드 리뷰). 화면이 src/lib/updateSafety.ts holdPendingSave 로 알린다.
   */
  pendingSaves?: number;
}

/**
 * 고객이 직접 채우는 공개 화면 — 로그인 없이 링크로 열고, 저장 전까지 입력이 브라우저에만 있다.
 * 여기서는 자동으로 절대 안 넘긴다(포커스 신호만으로는 "폰 키보드를 닫고 잠깐 멈춘" 고객을
 * 못 가른다). 읽기만 하는 공개 화면(시세표 공유 /quote, 앱 안내 /msg-forwarder, 로그인)은 뺀다.
 */
const CUSTOMER_INPUT_PREFIXES = [
  "/form/",
  "/form-simple/",
  "/f/",
  "/apply",
  "/signature-pad/",
  "/crm/signature-pad",
  "/policy-upload/",
  "/payback/",
  "/payback-apply/",
];

export function isCustomerInputPage(pathname: string): boolean {
  return CUSTOMER_INPUT_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

export interface UpdateSafetyVerdict {
  safe: boolean;
  reasons: UnsafeReason[];
}

/** 신호를 보고 "지금 새로고침해도 되나"를 판정한다. 이유는 심각한 순서대로 담긴다. */
export function assessUpdateSafety(s: UpdateSafetySignals): UpdateSafetyVerdict {
  const reasons: UnsafeReason[] = [];
  if (isCustomerInputPage(s.pathname) || (s.previousPathname && isCustomerInputPage(s.previousPathname))) {
    reasons.push("customer-page");
  }
  if (s.unloadGuarded) reasons.push("guarded-page");
  if (s.openModals > 0) reasons.push("modal-open");
  if (s.editing) reasons.push("editing");
  if (s.inFlightWrites > 0) reasons.push("request-in-flight");
  if ((s.pendingSaves ?? 0) > 0) reasons.push("save-pending");
  return { safe: reasons.length === 0, reasons };
}

/** 경로만 남긴다 — 물음표 뒤(창 `?modal=`·필터·탭)·# 뒤·끝 `/` 는 같은 화면이다. */
export function screenPath(pathOrUrl: string): string {
  const bare = pathOrUrl.split(/[?#]/, 1)[0] || "/";
  return bare.length > 1 ? bare.replace(/\/+$/, "") || "/" : bare;
}

/**
 * 화면을 옮겼나 — 직전 경로와 지금 경로가 다른 화면이면 true.
 * 첫 그림(직전 경로 없음)은 옮김이 아니다. 물음표 뒤만 바뀐 건 같은 화면이라 false.
 */
export function isScreenChange(previous: string | null | undefined, next: string): boolean {
  if (previous == null) return false;
  return screenPath(previous) !== screenPath(next);
}

/** 이만큼 아무 입력(마우스·키보드·스크롤·터치)이 없으면 «자리를 비웠다» 로 본다. */
export const AWAY_MS = 30 * 60 * 1000;

/** 자리를 비웠나 — 마지막 입력에서 문턱만큼 지났으면 true. */
export function isAway(now: number, lastActiveAt: number, thresholdMs: number = AWAY_MS): boolean {
  return now - lastActiveAt >= thresholdMs;
}
